/*
 * Validation for the OAuth redirect that eBay and Xero send the user back to.
 *
 * The security-critical part is the 'state' check. When a user starts a
 * connection we generate a single-use uuid, store it on their own oauth
 * document and pass it to the provider. The provider hands it back on the
 * redirect. If we do not compare the two, anyone can send a signed-in user a
 * link carrying their own authorisation code and have that account linked to
 * the user's tenant (OAuth CSRF / account injection).
 *
 * Kept as a pure function so both callback pages share one implementation and
 * it can be tested without standing up Firebase.
 */

export const INVALID_STATE_MESSAGE =
  'This authorisation link is not valid for your account. Please start the connection again.'

/**
 * @param params   parsed query string from the redirect
 * @param oauthDoc the user's oauth document data, or null when it is missing
 * @param platform display name used in the messages, e.g. 'Xero'
 * @returns {{valid: boolean, message?: string, stored?: object}}
 */
export const validateOauthCallback = (params, oauthDoc, platform) => {
  const query = params || {}

  // The provider redirects back with 'error' when the user refuses consent.
  if (query.error) {
    return {
      valid: false,
      message: `${platform} did not grant access: ${query.error}`
    }
  }

  if (!oauthDoc) {
    return {
      valid: false,
      message: `There is no ${platform} authorisation in progress for this account. Please start again.`
    }
  }

  const stored = oauthDoc.data || {}

  // Reject unless the returned state matches the one we issued for this user.
  // Note query-string yields an array for a repeated parameter, so the strict
  // comparison also rejects '?state=good&state=evil'.
  if (
    !oauthDoc.oauthInProgress ||
    !stored.state ||
    !query.state ||
    query.state !== stored.state
  ) {
    return { valid: false, message: INVALID_STATE_MESSAGE }
  }

  if (!query.code) {
    return {
      valid: false,
      message: `${platform} did not return an authorisation code. Please start the connection again.`
    }
  }

  return { valid: true, stored }
}
